import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CollectionsBookmarkRoundedIcon from '@mui/icons-material/CollectionsBookmarkRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import { useNavigate } from 'react-router-dom';

export default function ProfDashboard({ user }) {
  const navigate = useNavigate();
  const firstName = user?.prenom || user?.nom || 'prof';
  const isAdmin = user?.role === 'admin';

  const sections = [
    {
      title: 'Mes classes',
      description: 'Créer, organiser et ouvrir rapidement une classe.',
      icon: <SchoolRoundedIcon sx={{ fontSize: 30, color: '#2F5FFF' }} />,
      to: '/classes',
      accent: 'linear-gradient(135deg, #eef4ff 0%, #f7faff 100%)',
      borderColor: 'rgba(47,95,255,0.16)',
      iconBg: 'linear-gradient(135deg, rgba(47,95,255,0.16) 0%, rgba(138,180,255,0.22) 100%)'
    },
    {
      title: 'Activités & compétences',
      description: 'Préparer les référentiels et les barèmes.',
      icon: <CollectionsBookmarkRoundedIcon sx={{ fontSize: 30, color: '#7B3FF2' }} />,
      to: '/activites',
      accent: 'linear-gradient(135deg, #f5f0ff 0%, #fcf9ff 100%)',
      borderColor: 'rgba(123,63,242,0.16)',
      iconBg: 'linear-gradient(135deg, rgba(123,63,242,0.16) 0%, rgba(199,168,255,0.24) 100%)'
    },
    {
      title: 'Suivi des élèves',
      description: 'Évaluer plus vite par classe et par activité.',
      icon: <ChecklistRoundedIcon sx={{ fontSize: 30, color: '#0E9F6E' }} />,
      to: '/suivi-eleves',
      accent: 'linear-gradient(135deg, #eefbf5 0%, #f8fffb 100%)',
      borderColor: 'rgba(14,159,110,0.16)',
      iconBg: 'linear-gradient(135deg, rgba(14,159,110,0.16) 0%, rgba(109,214,171,0.24) 100%)'
    },
    {
      title: 'Applications',
      description: 'Accéder directement aux applications élèves et terrain.',
      icon: <AppsRoundedIcon sx={{ fontSize: 30, color: '#C026D3' }} />,
      to: '/apps',
      accent: 'linear-gradient(135deg, #fff0fb 0%, #fff8fe 100%)',
      borderColor: 'rgba(192,38,211,0.16)',
      iconBg: 'linear-gradient(135deg, rgba(192,38,211,0.14) 0%, rgba(244,114,182,0.18) 100%)'
    },
    ...(isAdmin ? [{
      title: 'Administration',
      description: 'Gérer les comptes enseignants et les accès.',
      icon: <AdminPanelSettingsRoundedIcon sx={{ fontSize: 30, color: '#C2410C' }} />,
      to: '/admin',
      accent: 'linear-gradient(135deg, #fff4ed 0%, #fffaf5 100%)',
      borderColor: 'rgba(194,65,12,0.16)',
      iconBg: 'linear-gradient(135deg, rgba(194,65,12,0.14) 0%, rgba(251,191,36,0.18) 100%)'
    }] : [])
  ];

  const cardSx = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: { xs: 3, sm: 4 },
    boxShadow: '0 10px 22px rgba(32,24,64,0.08)',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 14px 30px rgba(32,24,64,0.12)'
    }
  };

  return (
    <Box sx={{ px: { xs: 1.25, sm: 3 }, py: { xs: 1.5, sm: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.75 },
          mb: 2.5,
          borderRadius: { xs: 3, sm: 4 },
          background: 'linear-gradient(135deg, #faf7ff 0%, #eef6ff 52%, #f7fbff 100%)',
          border: '1px solid rgba(63,61,86,0.08)',
          boxShadow: '0 14px 30px rgba(63,61,86,0.08)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -40,
            right: -24,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(123,63,242,0.12) 0%, rgba(123,63,242,0) 70%)'
          }}
        />

        <Stack spacing={1.25} sx={{ position: 'relative' }}>
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
            <Chip label={`Bonjour ${firstName}`} sx={{ fontWeight: 700 }} />
            {isAdmin && <Chip color="error" label="Admin" sx={{ fontWeight: 700 }} />}
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: '#34205e',
                color: '#fff'
              }}
            >
              <AutoAwesomeRoundedIcon fontSize="small" />
            </Avatar>
          </Stack>

          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Tableau de bord enseignant
          </Typography>

          <Typography color="text.secondary">
            Retrouve tes outils de gestion, de suivi et les applications dédiées aux élèves.
          </Typography>
        </Stack>
      </Paper>

      <Grid container spacing={{ xs: 1.25, sm: 2 }}>
        {sections.map((section) => (
          <Grid item xs={12} sm={6} lg={4} key={section.title}>
            <Paper
              role="button"
              tabIndex={0}
              onClick={() => navigate(section.to)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(section.to);
                }
              }}
              elevation={0}
              sx={{
                ...cardSx,
                background: section.accent,
                border: `1px solid ${section.borderColor}`
              }}
            >
              <Stack direction="row" spacing={{ xs: 1.25, sm: 1.5 }} alignItems="flex-start">
                <Box
                  sx={{
                    width: { xs: 44, sm: 52 },
                    height: { xs: 44, sm: 52 },
                    borderRadius: { xs: 2.5, sm: 3 },
                    background: section.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)',
                    flexShrink: 0
                  }}
                >
                  {section.icon}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      mb: 0.35,
                      fontSize: { xs: '0.98rem', sm: '1.1rem' },
                      lineHeight: 1.2
                    }}
                  >
                    {section.title}
                  </Typography>

                  <Typography
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.84rem', sm: '0.95rem' },
                      lineHeight: 1.32
                    }}
                  >
                    {section.description}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}